import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, useFormikContext } from "formik";

import {
    bookingApi,
    locationApi,
    shopApi,
    motorcycleApi,
} from "./services/booking.api";
import { normalizeBookingForForm } from "./utils/bookingNormalizer";
import { usePricingPreview } from "./hooks/usePricingPreview";
import { useExchangeRate } from "./hooks/useExchangeRate";
import { useMotorcycleLoader } from "./hooks/useMotorcycleLoader";

import BookingLayout from "./components/BookingLayout";
import OverviewTab from "./components/tabs/OverviewTab";
import AssetsTab from "./components/tabs/AssetsTab";
import PricingTab from "./components/tabs/PricingTab";
import StatusTab from "./components/tabs/StatusTab";

const TABS = [
    {
        id: "overview",
        label: "Customer Information",
    },
    {
        id: "assets",
        label: "Rental Information",
    },
    {
        id: "pricing",
        label: "Pricing & Settlement",
    },
    {
        id: "status",
        label: "Status & History",
    },
];

// -------------------- Inner Form --------------------
const InnerForm = ({
    activeTab,
    setActiveTab,
    tabs,
    isEdit,
    originalRentalOrderExtra,
    motorcycleOptions,
    setMotorcycleOptions,
    isHydrating,
    insuranceQuotes,
    setInsuranceQuotes,
    rentalOrderHistory,
}) => {
    const {
        values,
        setValues,
        setFieldValue,
        handleSubmit,
        isSubmitting,
    } = useFormikContext();

    const [countryOptions, setCountryOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [shopsOptions, setShopsOptions] = useState([]);
    const [shopMotorcycleExtras, setShopMotorcycleExtras] = useState([]);

    const originalServicesTotalCentsRef = useRef(null);
    const bootstrappedRef = useRef(false);

    const pickupCountryId =
        values?.shopSnapshot?.pickup?.location?.country?.id;

    const pickupCityId =
        values?.shopSnapshot?.pickup?.location?.city?.id;

    const pickupLocationId =
        values?.shopSnapshot?.pickup?.location?.id;

    const pickupShopId =
        values?.shopSnapshot?.pickup?.shop?.id;

    const shopMotorcycleId =
        values?.motorcycleSnapshot?.shopMotorcycle?.id;

    const extrasTotalWithFeesCents =
        values?.pricingSnapshot?.calculation?.extrasTotalWithFeesCents;

    const supplierCurrency =
        values?.pricingSnapshot?.calculation?.supplierCurrencyCode;

    const customerCurrency =
        values?.pricingSnapshot?.calculation?.customerCurrencyCode;

    // 记录原始 services total
    useEffect(() => {
        if (isHydrating) {
            return;
        }

        if (originalServicesTotalCentsRef.current != null) {
            return;
        }

        originalServicesTotalCentsRef.current =
            extrasTotalWithFeesCents ?? 0;
    }, [
        isHydrating,
        extrasTotalWithFeesCents,
    ]);

    // 汇率 hook
    useExchangeRate({
        supplierCurrency,
        customerCurrency,
        setFieldValue,
        isHydrating,
    });

    // Pricing preview hook
    const pricing = usePricingPreview({
        values,
        setValues,
        isHydrating,
        setInsuranceQuotes,
    });

    const {
        recalculate,
        needsRecalc,
        isPricingLoading,
        lastError,
        markDirtyPricing,
    } = pricing;

    const fetchCountries = useCallback(async () => {
        try {
            const data =
                await locationApi.getCountries();

            return data || [];
        } catch (e) {
            console.error(
                "Failed to fetch countries:",
                e,
            );

            return [];
        }
    }, []);

    useEffect(() => {
        let mounted = true;

        const loadCountries = async () => {
            const countries =
                await fetchCountries();

            if (mounted) {
                setCountryOptions(countries);
            }
        };

        loadCountries();

        return () => {
            mounted = false;
        };
    }, [fetchCountries]);

    const bootstrapOptions = useCallback(async () => {
        const result = {
            cities: null,
            locations: null,
            shops: null,
            motorcycles: null,
        };

        if (pickupCountryId) {
            result.cities =
                await locationApi.getCities(
                    pickupCountryId,
                );
        }

        if (pickupCityId) {
            result.locations =
                await locationApi.getLocations(
                    pickupCityId,
                );
        }

        if (pickupLocationId) {
            result.shops =
                await shopApi.getByLocation(
                    pickupLocationId,
                );
        }

        if (pickupShopId) {
            const data =
                await shopApi.getById(
                    pickupShopId,
                );

            result.motorcycles = (
                data?.shopMotorcycleInfo || []
            ).map((m) => ({
                value: m.id,
                label: `${m.brandName} ${m.modelName}`,
            }));
        }

        return result;
    }, [
        pickupCountryId,
        pickupCityId,
        pickupLocationId,
        pickupShopId,
    ]);

    // 首次进入时，根据 snapshot 回填 options
    useEffect(() => {
        if (bootstrappedRef.current) {
            return;
        }

        let mounted = true;

        const bootstrap = async () => {
            try {
                const {
                    cities,
                    locations,
                    shops,
                    motorcycles,
                } = await bootstrapOptions();

                if (!mounted) {
                    return;
                }

                if (cities !== null) {
                    setCityOptions(
                        cities || [],
                    );
                }

                if (locations !== null) {
                    setLocationOptions(
                        locations || [],
                    );
                }

                if (shops !== null) {
                    setShopsOptions(
                        shops || [],
                    );
                }

                if (motorcycles !== null) {
                    setMotorcycleOptions(
                        motorcycles,
                    );
                }

                bootstrappedRef.current = true;
            } catch (err) {
                console.error(
                    "bootstrap failed",
                    err,
                );
            }
        };

        bootstrap();

        return () => {
            mounted = false;
        };
    }, [
        bootstrapOptions,
        setMotorcycleOptions,
    ]);

    useMotorcycleLoader({
        shopMotorcycleId,
        isHydrating,
        setValues,
        setShopMotorcycleExtras,
        fetchShopMotorcycle:
            motorcycleApi.getShopMotorcycle,
    });

    const handleTabChange = useCallback(
        (tabId) => {
            setActiveTab(tabId);

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        },
        [setActiveTab],
    );

    return (
        <BookingLayout
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            values={values}
            recalculate={recalculate}
            needsRecalc={needsRecalc}
            isPricingLoading={isPricingLoading}
            isHydrating={isHydrating}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
        >
            {activeTab === "overview" && (
                <OverviewTab
                    isEdit={isEdit}
                    countryOptions={
                        countryOptions
                    }
                />
            )}

            {activeTab === "assets" && (
                <AssetsTab
                    shopMotorcycleExtras={
                        shopMotorcycleExtras
                    }
                    countryOptions={
                        countryOptions
                    }
                    originalRentalOrderExtra={
                        originalRentalOrderExtra
                    }
                    cityOptions={
                        cityOptions
                    }
                    setCityOptions={
                        setCityOptions
                    }
                    locationOptions={
                        locationOptions
                    }
                    setLocationOptions={
                        setLocationOptions
                    }
                    shopsOptions={
                        shopsOptions
                    }
                    setShopsOptions={
                        setShopsOptions
                    }
                    motorcycleOptions={
                        motorcycleOptions
                    }
                    setMotorcycleOptions={
                        setMotorcycleOptions
                    }
                    markDirtyPricing={
                        markDirtyPricing
                    }
                    originalServicesTotalCentsRef={
                        originalServicesTotalCentsRef
                    }
                />
            )}

            {activeTab === "pricing" && (
                <PricingTab
                    insuranceQuotes={
                        insuranceQuotes
                    }
                    recalculate={
                        recalculate
                    }
                    needsRecalc={
                        needsRecalc
                    }
                    markDirtyPricing={
                        markDirtyPricing
                    }
                    isPricingLoading={
                        isPricingLoading
                    }
                    lastError={
                        lastError
                    }
                    isHydrating={
                        isHydrating
                    }
                />
            )}

            {activeTab === "status" && (
                <StatusTab
                    rentalOrderHistory={
                        rentalOrderHistory
                    }
                />
            )}
        </BookingLayout>
    );
};

// -------------------- Booking Form --------------------
const BookingForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const isEdit = Boolean(id);

    const [isHydrating, setIsHydrating] =
        useState(true);

    const [activeTab, setActiveTab] =
        useState("overview");

    const [initialValues, setInitialValues] =
        useState(null);

    const [
        originalRentalOrderExtra,
        setOriginalRentalOrderExtra,
    ] = useState([]);

    const [
        motorcycleOptions,
        setMotorcycleOptions,
    ] = useState([]);

    const [
        insuranceQuotes,
        setInsuranceQuotes,
    ] = useState([]);

    const [
        rentalOrderHistory,
        setRentalOrderHistory,
    ] = useState([]);

    const [loading, setLoading] =
        useState(isEdit);

    const [error, setError] =
        useState(null);

    const handleSubmit = useCallback(
        async (values) => {
            try {
                if (id) {
                    await bookingApi.update(
                        id,
                        values,
                    );
                } else {
                    await bookingApi.create(
                        values,
                    );
                }

                alert(
                    "Booking saved successfully!",
                );

                // 如果之后需要保存成功跳转：
                // navigate("/bookings");
            } catch (err) {
                console.error(
                    "Save booking error:",
                    err,
                );
            }
        },
        [id],
    );

    const loadBooking = useCallback(async () => {
        if (!isEdit) {
            const data =
                await bookingApi.getMeta();

            return {
                initialValues:
                    data?.defaults || {},
                originalRentalOrderExtra:
                    data?.defaults
                        ?.rentalOrderExtra || [],
                rentalOrderHistory: [],
                insuranceQuotes: [],
                motorcycleOptions: [],
            };
        }

        const data =
            await bookingApi.getById(id);

        const normalized =
            normalizeBookingForForm(data);

        return {
            initialValues:
                normalized,
            rentalOrderHistory:
                data?.rentalOrderHistory ||
                [],
            originalRentalOrderExtra:
                data?.rentalOrderExtra ||
                [],
            insuranceQuotes:
                data?.meta?.options
                    ?.insuranceQuotes || [],
            motorcycleOptions:
                data?.meta?.options
                    ?.shopMotorcycles || [],
        };
    }, [
        id,
        isEdit,
    ]);

    useEffect(() => {
        let mounted = true;
        let frameId = null;

        const hydrateBooking = async () => {
            try {
                setError(null);
                setLoading(true);
                setIsHydrating(true);

                const data =
                    await loadBooking();

                if (!mounted) {
                    return;
                }

                setInitialValues(
                    data.initialValues,
                );

                setOriginalRentalOrderExtra(
                    data.originalRentalOrderExtra,
                );

                setRentalOrderHistory(
                    data.rentalOrderHistory,
                );

                setInsuranceQuotes(
                    data.insuranceQuotes,
                );

                setMotorcycleOptions(
                    data.motorcycleOptions,
                );

                frameId =
                    requestAnimationFrame(
                        () => {
                            if (
                                mounted
                            ) {
                                setIsHydrating(
                                    false,
                                );
                            }
                        },
                    );
            } catch (err) {
                console.error(err);

                if (mounted) {
                    setError(
                        "Failed to load data",
                    );
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        hydrateBooking();

        return () => {
            mounted = false;

            if (frameId !== null) {
                cancelAnimationFrame(
                    frameId,
                );
            }
        };
    }, [loadBooking]);

    if (loading) {
        return (
            <div>
                Loading...
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    padding: 12,
                }}
            >
                {error}
            </div>
        );
    }

    if (!initialValues) {
        return (
            <div>
                Loading data...
            </div>
        );
    }

    return (
        <Formik
            initialValues={
                initialValues
            }
            onSubmit={
                handleSubmit
            }
            validateOnChange={
                false
            }
            validateOnBlur
            enableReinitialize
        >
            <InnerForm
                activeTab={
                    activeTab
                }
                setActiveTab={
                    setActiveTab
                }
                tabs={TABS}
                isEdit={
                    isEdit
                }
                originalRentalOrderExtra={
                    originalRentalOrderExtra
                }
                motorcycleOptions={
                    motorcycleOptions
                }
                setMotorcycleOptions={
                    setMotorcycleOptions
                }
                isHydrating={
                    isHydrating
                }
                insuranceQuotes={
                    insuranceQuotes
                }
                setInsuranceQuotes={
                    setInsuranceQuotes
                }
                rentalOrderHistory={
                    rentalOrderHistory
                }
            />
        </Formik>
    );
};

export default BookingForm;